import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { knowledgeBaseService } from '../services/knowledgeBaseService';
import type { KnowledgeBase } from '../services/knowledgeBaseService';
import { useAuth } from '../hooks/useAuth';
import {
  extractionService,
  type ExtractionCostEstimate,
} from '../services/extractionService';

interface Props {
  isOpen: boolean;
  subProjectId: string;
  forcedRegime: 'A' | 'B';
  onClose: () => void;
  onLaunched: (transcriptId: string) => void;
}

type Step = 'select_kb' | 'confirm_cost' | 'launching';

const ExtractionLaunchModal: React.FC<Props> = ({ isOpen, subProjectId, forcedRegime, onClose, onLaunched }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Cost estimate is shown only to SysAdmin/DevOps; other roles launch directly.
  const canSeeCostEstimate = user?.role === 'SysAdmin' || user?.role === 'DevOps';

  const [step, setStep] = useState<Step>('select_kb');
  const [availableKBs, setAvailableKBs] = useState<KnowledgeBase[]>([]);
  const [loadingKBs, setLoadingKBs] = useState(false);
  const [selectedKbId, setSelectedKbId] = useState('');

  const [intent, setIntent] = useState('');
  const [mustHaveKeywords, setMustHaveKeywords] = useState('');
  const [destinationKbId, setDestinationKbId] = useState('');

  const [costEstimate, setCostEstimate] = useState<ExtractionCostEstimate | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setStep('select_kb');
    setSelectedKbId('');
    setIntent('');
    setMustHaveKeywords('');
    setDestinationKbId('');
    setCostEstimate(null);
    setError(null);

    const loadKBs = async () => {
      setLoadingKBs(true);
      try {
        const all = await knowledgeBaseService.getAll();
        setAvailableKBs(all);
        if (all.length === 1) {
          setSelectedKbId(all[0].id);
        }
      } catch {
        // show empty list
      } finally {
        setLoadingKBs(false);
      }
    };
    loadKBs();
  }, [isOpen]);

  const handleKbChange = (kbId: string) => {
    setSelectedKbId(kbId);
    setCostEstimate(null);
    setError(null);
  };

  const handleEstimate = async () => {
    if (!selectedKbId || !intent.trim()) return;
    setLoadingEstimate(true);
    setError(null);
    try {
      const est = await extractionService.estimateCost(subProjectId, selectedKbId, forcedRegime);
      setCostEstimate(est);
      setStep('confirm_cost');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to estimate cost';
      setError(msg);
    } finally {
      setLoadingEstimate(false);
    }
  };

  const handleLaunch = async () => {
    if (!selectedKbId || !intent.trim()) return;
    setLaunching(true);
    setError(null);
    setStep('launching');
    try {
      const keywords = mustHaveKeywords
        .split(',')
        .map(k => k.trim())
        .filter(Boolean);
      const res = await extractionService.launch(subProjectId, {
        kb_id: selectedKbId,
        intent: intent.trim(),
        target_context: 'state_of_the_art',
        destination_kb_id: destinationKbId || undefined,
        must_have_keywords: keywords,
        forced_regime: forcedRegime,
      });
      onLaunched(res.transcript_id);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to launch extraction';
      setError(msg);
      // Return to the step the user launched from (non-admins never see the cost step).
      setStep(canSeeCostEstimate ? 'confirm_cost' : 'select_kb');
    } finally {
      setLaunching(false);
    }
  };

  const formatCost = (cents: number, currency: string) => {
    const amount = cents / 100;
    return `${currency === 'EUR' ? '€' : '$'}${amount.toFixed(2)}`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-base font-semibold">
            {forcedRegime === 'A'
              ? t('extraction.modalTitleSlow', 'DPDS — Document-per-Document Search')
              : t('extraction.modalTitleFast', 'RAGS — Deep KB Retrieval')}
          </h2>
          <span className="text-slate-400 hover:text-slate-600 text-xl cursor-pointer" onClick={onClose}>&times;</span>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto flex-1">

          {/* Step 1: Configure extraction */}
          {(step === 'select_kb') && (
            <>
              <p className="text-sm text-slate-600">
                {t('extraction.hint', 'Select a source Knowledge Base and describe what you are looking for. Relevant documents will be available for export to the destination KB.')}
              </p>

              {/* Source KB */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">{t('extraction.sourceKb', 'Source Knowledge Base')}</label>
                {loadingKBs ? (
                  <p className="text-sm text-slate-400">{t('common.loading', 'Loading...')}</p>
                ) : availableKBs.length === 0 ? (
                  <p className="text-sm text-slate-500">{t('extraction.noKbs', 'No Knowledge Bases available.')}</p>
                ) : (
                  <select
                    className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={selectedKbId}
                    onChange={(e) => handleKbChange(e.target.value)}
                  >
                    <option value="" disabled>{t('extraction.selectKbPlaceholder', 'Select a Knowledge Base...')}</option>
                    {availableKBs.map(kb => (
                      <option key={kb.id} value={kb.id}>
                        {kb.name} ({kb.document_count ?? 0} {t('extraction.docs', 'docs')})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Regime banner — reflects the user's explicit retrieval choice */}
              {forcedRegime === 'A' && (
                <div className="rounded bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-800">
                  <strong>{t('extraction.regimeA.label', 'DPDS — Document-per-Document Search')}</strong>
                  {' '}
                  {t('extraction.regimeA.description', 'Every document in this KB will be individually assessed by AI for relevance. Max 5000 documents.')}
                </div>
              )}
              {forcedRegime === 'B' && (
                <div className="rounded bg-violet-50 border border-violet-200 px-3 py-2 text-xs text-violet-800">
                  <strong>{t('extraction.regimeB.label', 'RAGS — Deep KB Retrieval')}</strong>
                  {' '}
                  {t('extraction.regimeB.description', 'Documents ranked using combined keyword and semantic similarity search, then top results individually assessed. Up to 4000 candidates.')}
                </div>
              )}

              {/* Fields — shown only after KB is selected */}
              {selectedKbId && (
                <>
                  {/* Intent */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {t('extraction.intent', 'Intent')} <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      rows={3}
                      placeholder={t('extraction.intentPlaceholder', 'Describe what you are looking for in 1–3 sentences...')}
                      value={intent}
                      onChange={(e) => setIntent(e.target.value)}
                      maxLength={1000}
                    />
                  </div>

                  {/* Must-have keywords */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      {t('extraction.keywords', 'Must-have keywords')}
                      <span className="ml-1 text-slate-400 font-normal">{t('extraction.optional', '(optional, comma-separated)')}</span>
                    </label>
                    <input
                      type="text"
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder={t('extraction.keywordsPlaceholder', 'e.g. thermal, heat exchanger, coolant')}
                      value={mustHaveKeywords}
                      onChange={(e) => setMustHaveKeywords(e.target.value)}
                    />
                  </div>

                  {/* Destination KB */}
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">{t('extraction.destinationKb', 'Destination Knowledge Base')}</label>
                    <select
                      className="w-full border border-slate-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      value={destinationKbId}
                      onChange={(e) => setDestinationKbId(e.target.value)}
                    >
                      <option value="">{t('extraction.destinationKbNone', '— None (review results only)')}</option>
                      {availableKBs.filter(kb => kb.id !== selectedKbId).map(kb => (
                        <option key={kb.id} value={kb.id}>{kb.name} ({kb.document_count ?? 0} docs)</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-slate-400">
                      {t('extraction.destinationKbHint', 'Selected relevant documents will be added to this KB after review.')}
                    </p>
                  </div>
                </>
              )}
            </>
          )}

          {/* Step 2: Cost confirmation */}
          {step === 'confirm_cost' && costEstimate && (
            <>
              <p className="text-sm font-medium">{t('extraction.costEstimateTitle', 'Cost Estimate')}</p>
              <div className="bg-slate-50 rounded p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span>{t('extraction.costDocs', 'Documents in KB')}</span>
                  <span>{costEstimate.total_documents.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('extraction.costRegime', 'Search strategy')}</span>
                  <span>{costEstimate.regime_description}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('extraction.costLlmCalls', 'Estimated LLM calls')}</span>
                  <span>{costEstimate.estimated_llm_calls.toLocaleString()}</span>
                </div>
                <div className="flex justify-between font-semibold text-base pt-1 border-t border-slate-200 mt-1">
                  <span>{t('extraction.costEstimated', 'Estimated cost')}</span>
                  <span>{formatCost(costEstimate.estimated_cost_cents, costEstimate.currency)}</span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  {t('extraction.costModel', 'Model:')} {costEstimate.model_name || costEstimate.model_id}
                </p>
              </div>
              <p className="text-xs text-slate-400">
                {t('extraction.costDisclaimer', 'This is an estimate. Actual cost depends on document sizes and LLM responses.')}
              </p>
            </>
          )}

          {/* Launching */}
          {step === 'launching' && (
            <p className="text-sm text-slate-600">{t('extraction.launching', 'Launching extraction — the results will open in a new tab...')}</p>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <button
            type="button"
            className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
            onClick={onClose}
            disabled={step === 'launching'}
          >
            {t('common.cancel', 'Cancel')}
          </button>

          {step === 'select_kb' && canSeeCostEstimate && (
            <button
              type="button"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              onClick={handleEstimate}
              disabled={!selectedKbId || !intent.trim() || loadingEstimate}
            >
              {loadingEstimate
                ? t('extraction.estimating', 'Estimating...')
                : t('extraction.nextEstimate', 'Estimate Cost')}
            </button>
          )}

          {step === 'select_kb' && !canSeeCostEstimate && (
            <button
              type="button"
              className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
              onClick={handleLaunch}
              disabled={!selectedKbId || !intent.trim() || launching}
            >
              {launching
                ? t('extraction.launching', 'Launching...')
                : t('extraction.launch', 'Launch')}
            </button>
          )}

          {step === 'confirm_cost' && (
            <>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200"
                onClick={() => setStep('select_kb')}
              >
                {t('common.back', 'Back')}
              </button>
              <button
                type="button"
                className="px-4 py-2 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                onClick={handleLaunch}
                disabled={launching}
              >
                {launching
                  ? t('extraction.launching', 'Launching...')
                  : t('extraction.confirmLaunch', 'Confirm & Launch')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExtractionLaunchModal;
